import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PawPrint, CalendarDays, ShieldAlert } from "lucide-react";
import { Btn, Card, Field, Textarea, PageTitle, StatusChip, DemoBadge, Tag, Meta, Notice, Empty, cx } from "../components/kit";
import { Loading, NoAccess, DraftBanner } from "../components/common";
import FileUploader from "../components/FileUploader";
import { api, parseError } from "../lib/api";
import { useDraft, useOnline } from "../lib/draft";
import { fmtDate, fmtDateTime, fmtLocal } from "../lib/format";

export function OwnerHome() {
  const { t } = useTranslation();
  const [dogs, setDogs] = useState(null);
  useEffect(() => { api.get("/owner/dogs").then((r) => setDogs(r.data)); }, []);
  if (!dogs) return <Loading />;
  return (
    <div className="mx-auto max-w-xl">
      <PageTitle title={t("owner.title")} sub={t("owner.sub")} />
      {dogs.length === 0 ? <Empty testid="owner-empty" title={t("owner.empty")} /> : (
        <ul className="space-y-3">{dogs.map((d) => (
          <li key={d.id}><Link to={`/owner/dogs/${d.id}`} data-testid={`owner-dog-${d.number}`} className="lift flex items-center gap-4 rounded-2xl border bg-white p-4">
            <span className="rounded-2xl bg-[hsl(var(--peach))] p-3"><PawPrint className="h-6 w-6 text-[hsl(14_56%_34%)]" /></span>
            <span className="min-w-0 flex-1"><span className="block font-display text-lg font-bold">{d.name}</span><span className="text-sm text-muted-foreground">{d.groomer_name} · {d.workspace_name}</span></span>
            <span className="text-sm font-semibold">{t("owner.open_tasks", { count: d.open_tasks })}</span>
          </Link></li>))}</ul>
      )}
    </div>
  );
}

export function OwnerDog() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  useEffect(() => { api.get(`/owner/dogs/${id}`).then((r) => setD(r.data)).catch((e) => parseError(e).status === 404 && setDenied(true)); }, [id]);
  if (denied) return <NoAccess />;
  if (!d) return <Loading />;
  const r = d.latest_report;
  return (
    <div className="mx-auto max-w-xl space-y-4" data-testid="owner-dog-page">
      <PageTitle title={d.dog.name} badge={d.dog.is_demo && <DemoBadge />} sub={`${d.groomer_name} · ${d.workspace_name}`} />
      <Card data-testid="owner-plan"><h2 className="mb-2 text-lg font-bold">{t("owner.plan")}</h2>
        {d.plan.length === 0 ? <p className="text-sm text-muted-foreground">{t("owner.no_plan")}</p> : d.plan.map((p) => (
          <div key={p.id} className="text-sm"><p className="font-semibold">{p.title} <Tag>{p.number}</Tag> <Tag>{t("programme.edition_n", { n: p.edition })}</Tag></p><p className="mt-1">{p.directions.map((x) => `${x} — ${t(`dir.${x}`)}`).join("; ")}</p></div>))}
      </Card>
      <Card data-testid="owner-latest-report"><h2 className="mb-2 text-lg font-bold">{t("owner.latest_report")}</h2>
        {!r ? <p className="text-sm text-muted-foreground">{t("owner.no_report")}</p> : <div className="space-y-1 text-sm">
          <p className="font-semibold">{r.number} · {fmtDate(r.actual_date, r.date_precision)}</p>
          <p>{t("send.done")}: {r.completed.join(", ") || "—"}</p>
          {r.not_completed.length > 0 && <p>{t("owner.not_done")}: {r.not_completed.map((x) => `${x.title}${x.reason ? ` (${x.reason})` : ""}`).join(", ")}</p>}
          {d.latest_instruction?.message && <p className="rounded-xl bg-muted p-3">{d.latest_instruction.message}</p>}
        </div>}
      </Card>
      <Card data-testid="owner-next-contact" className="flex items-start gap-3"><CalendarDays className="mt-1 h-5 w-5 shrink-0" /><div><h2 className="font-bold">{t("owner.next_contact")}</h2>
        <p className="text-sm">{d.latest_instruction?.next_contact || (d.next_visit ? fmtLocal(d.next_visit.scheduled_local, d.next_visit.time_zone) : t("owner.no_next"))}</p></div></Card>
      <h2 className="pt-2 text-xl font-bold">{t("owner.home_tasks")}</h2>
      {d.tasks.length === 0 && <p className="text-muted-foreground">{t("owner.no_tasks")}</p>}
      <ul className="space-y-3">{d.tasks.map((x) => (
        <li key={x.id}><Link to={`/owner/tasks/${x.id}`} data-testid={`owner-task-${x.number}`} className="lift block rounded-2xl border bg-white p-4">
          <div className="flex flex-wrap items-center gap-2"><b className="font-display text-lg">{x.content.title}</b><Tag>{x.number} · {t("common.version_n", { n: x.version })}</Tag></div>
          <p className="mt-1 text-sm text-muted-foreground">{t("task.deadline")}: {fmtDate(x.deadline)}</p>
          <div className="mt-2"><StatusChip status={x.latest_response || "no_response"} label={x.latest_response ? undefined : t("status.no_response")} /></div>
        </Link></li>))}</ul>
      {d.products.length > 0 && <Card data-testid="owner-products"><h2 className="mb-2 text-lg font-bold">{t("owner.products")}</h2><ul className="text-sm">{d.products.map((p) => <li key={p.id}>{p.name}</li>)}</ul></Card>}
      {d.earlier_tasks.length > 0 && <Card><h2 className="mb-2 text-lg font-bold">{t("owner.earlier")}</h2><ul className="space-y-2 text-sm">{d.earlier_tasks.map((x) => <li key={x.id}><Link className="flex flex-wrap items-center gap-2 underline-offset-4 hover:underline" to={`/owner/tasks/${x.id}`}>{x.number} v{x.version} · {x.title}<StatusChip status={x.status} /></Link></li>)}</ul></Card>}
    </div>
  );
}

const RESP = ["succeeded", "partly_succeeded", "did_not_succeed", "not_attempted", "help_needed"];

export function OwnerTask() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [x, setX] = useState(null);
  const [denied, setDenied] = useState(false);
  const online = useOnline();
  const draft = useDraft(`owner-response-${id}`, { outcome: null, context: "", observations: "", file_ids: [] });
  const f = draft.state;
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get(`/owner/tasks/${id}`).then((r) => setX(r.data)).catch((e) => parseError(e).status === 404 && setDenied(true)), [id]);
  useEffect(() => { load(); }, [load]);
  if (denied) return <NoAccess />;
  if (!x) return <Loading />;
  const c = x.content;
  const submit = async () => {
    if (!f.outcome) return setErr("required");
    if (!online) return draft.setPending(true);
    try {
      await api.post(`/owner/tasks/${id}/responses`, { ...f, client_id: draft.clientId });
      draft.reset({ outcome: null, context: "", observations: "", file_ids: [] });
      toast.success(t("owner.response_saved"));
      setErr(null);
      load();
    } catch (e) { const p = parseError(e); if (p.network) draft.setPending(true); else setErr(p.code); }
  };
  return (
    <div className="mx-auto max-w-xl space-y-4" data-testid="owner-task-page">
      <Link to={`/owner/dogs/${x.dog_id}`} className="inline-flex min-h-[44px] items-center text-sm font-semibold text-primary underline">{t("owner.back_to_dog")}</Link>
      {x.notice && (
        <Notice tone="warn" testid="task-change-notice">
          <p className="font-bold">{t(`owner.notice_${x.notice.status}`)}</p><p>{t("common.reason")}: {x.notice.reason}</p>
          {x.notice.current_task_id ? <Link to={`/owner/tasks/${x.notice.current_task_id}`} data-testid="task-current-edition-link" className="mt-2 inline-flex min-h-[44px] items-center font-semibold underline">{t("owner.open_current", { n: x.notice.current_version })}</Link> : x.notice.status === "replaced" && <p>{t("owner.current_pending")}</p>}
        </Notice>
      )}
      <Card>
        <div className="flex flex-wrap items-center gap-2"><StatusChip status={x.status} /><Tag>{x.number} · {t("common.version_n", { n: x.version })}</Tag></div>
        <h1 className="mt-2 font-display text-2xl font-extrabold break-words">{c.title}</h1>
        <p className="mt-3 whitespace-pre-wrap" lang={c.language} data-testid="owner-task-text">{c.text}</p>
        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <Meta label={t("owner.programme")}>{x.programme_number} · {t("programme.edition_n", { n: x.programme_edition })}</Meta>
          <Meta label={t("programme.directions")}>{x.directions.join(", ")}</Meta>
          <Meta label={t("owner.author")}>{x.author_name}</Meta>
          <Meta label={t("task.deadline")} testid="owner-task-deadline">{fmtDate(x.deadline)}</Meta>
          <Meta label={t("home_field.duration")}>{c.duration}</Meta><Meta label={t("home_field.frequency")}>{c.frequency}</Meta>
          <Meta label={t("owner.products")}>{c.products.map((p) => p.name).join(", ")}</Meta>
          <Meta label={t("task.reporting_method")}>{x.reporting_method}</Meta>
        </dl>
        <div className="mt-4 rounded-xl border border-[hsl(0_45%_84%)] bg-[hsl(0_70%_97%)] p-3 text-sm" data-testid="owner-stopping-conditions"><p className="flex items-center gap-2 font-bold"><ShieldAlert className="h-4 w-4" />{t("home_field.stopping_conditions")}</p><p>{c.stopping_conditions}</p></div>
      </Card>
      {x.help_pending && <Notice tone="warn" testid="help-pending-notice">{t("owner.help_pending")} <b>{c.stopping_conditions}</b></Notice>}
      {x.status === "sent" && (
        <Card className="space-y-3" data-testid="owner-response-form">
          <h2 className="text-lg font-bold">{t("owner.respond")}</h2>
          <DraftBanner pending={draft.pending} restored={draft.restored} dirty={!!f.outcome || !!f.context} />
          <div className="grid gap-2" role="radiogroup" aria-label={t("owner.respond")}>
            {RESP.map((o) => <button key={o} role="radio" aria-checked={f.outcome === o} data-testid={`owner-response-${o}`} onClick={() => { draft.setState({ ...f, outcome: o }); setErr(null); }}
              className={cx("min-h-[52px] rounded-2xl border-2 bg-white px-4 text-left text-base font-semibold", f.outcome === o ? "border-primary bg-[hsl(var(--peach))]" : "border-border")}>{t(`status.${o}`)}</button>)}
          </div>
          {err && <p role="alert" className="text-sm font-semibold text-destructive">{t(`err.${err}`)}</p>}
          <Field label={t("owner.context")} htmlFor="o-ctx"><Textarea id="o-ctx" data-testid="owner-context-input" value={f.context} onChange={(e) => draft.setState({ ...f, context: e.target.value })} /></Field>
          <Field label={t("owner.observations")} htmlFor="o-obs"><Textarea id="o-obs" data-testid="owner-observations-input" value={f.observations} onChange={(e) => draft.setState({ ...f, observations: e.target.value })} /></Field>
          <FileUploader dogId={x.dog_id} testid="owner-uploader" meta={{ title: c.title }} onAdded={(file) => draft.setState((s) => ({ ...s, file_ids: [...s.file_ids, file.id] }))} />
          <Btn className="w-full min-h-[52px]" testid="owner-response-submit-btn" onClick={submit}>{t("owner.send_response")}</Btn>
        </Card>
      )}
      {x.responses.length > 0 && <Card><h2 className="mb-2 text-lg font-bold">{t("owner.your_responses")}</h2>
        <ul className="space-y-2" data-testid="owner-responses">{x.responses.map((r) => <li key={r.id} className="rounded-xl border bg-white p-3 text-sm"><StatusChip status={r.outcome} /> <span className="text-muted-foreground">{fmtDateTime(r.created_at)}</span>{r.context && <p className="mt-1">{r.context}</p>}{r.review_status === "reviewed" && <p className="mt-1 text-xs">{t("owner.reviewed")}</p>}</li>)}</ul></Card>}
    </div>
  );
}
